import React, { useEffect } from 'react';
import { JSMABridge } from '@indeed/jsma-js-bridge';

/**
 * GlobalLinkNativeAppInterceptor Component
 *
 * Intercepts link clicks in native app webviews and opens them in separate windows
 * Based on smart-apply-webapp implementation for proper in-app navigation
 *
 * This component:
 * 1. Detects if running in a native app webview using JSMABridge
 * 2. Intercepts clicks on links with target="_blank"
 * 3. Opens links in native app modal webviews instead of browser tabs
 * 4. Excludes attachment/download links from interception
 *
 * @see smart-apply-webapp: web/basecamp/static/scripts/smartapply/applyForm/navigation/GlobalLinkNativeAppInterceptor.tsx
 */
export const GlobalLinkNativeAppInterceptor: React.FunctionComponent = () => {
    // Allowlist of paths that should open directly (not intercepted)
    const INDEEDAPPLY_PREVIEW_FILE_DOWNLOAD_PATHS = [
        'indeedapply/applicationpreview/indeedresume',
        'indeedapply/applicationpreview/attachment',
    ];
    const RESUME_DOWNLOAD_PATHS_PATTERN = /^\/rdp\/\w+\/pdf$/;
    const MESSAGING_PATHS_PATH = /^\/messaging\/.+/;
    const MYINDEED_RESUME_DOWNLOAD_PATHS = ['api/3/profile/files/resume/mostRecent'];

    /**
     * Get pathname without trailing slash for consistent matching
     */
    const getPathWithoutTrailingSlash = (url: URL): string | null => {
        const path = url.pathname;
        if (!path) {
            return null;
        }
        if (path.endsWith('/')) {
            return path.substring(0, path.length - 1);
        }
        return path;
    };

    /**
     * Check if URL is in attachment allowlist (should not be intercepted)
     */
    const isUrlInAttachmentAllowlist = (url: string): boolean => {
        try {
            const uri = new URL(url);
            const path = getPathWithoutTrailingSlash(uri);
            if (!path) {
                return false;
            }
            return (
                INDEEDAPPLY_PREVIEW_FILE_DOWNLOAD_PATHS.some((allowedPath) =>
                    path.includes(allowedPath),
                ) ||
                RESUME_DOWNLOAD_PATHS_PATTERN.test(path) ||
                MESSAGING_PATHS_PATH.test(path) ||
                MYINDEED_RESUME_DOWNLOAD_PATHS.some((allowedPath) => path.includes(allowedPath))
            );
        } catch {
            return false;
        }
    };

    useEffect(() => {
        // Only activate in native app webview
        if (!JSMABridge.isAvailable()) {
            return;
        }

        /**
         * Handle link clicks in native app webview
         * Intercepts target="_blank" links and opens them in native modal
         */
        const handleLinkClick = async (e: MouseEvent) => {
            const anchorElm = (e.target as HTMLElement).closest('a');
            if (
                !!anchorElm &&
                anchorElm.target === '_blank' &&
                !isUrlInAttachmentAllowlist(anchorElm.href)
            ) {
                // Override the anchor click events for native app
                // So that links can be opened in a separate window (especially a fix for Android devices)
                // Exclude the links on the preview section, which is rendered by preview service
                e.preventDefault();
                await JSMABridge.showModalWithWebViewAndWaitForResult({
                    url: anchorElm.href,
                    presentationMode: 'FULLSCREEN',
                });
            }
        };

        // Only add listener for Android platform (iOS handles this differently)
        JSMABridge.getNativeAppInfo()
            .then((info) => {
                if (info.platform === 'ANDROID') {
                    document.addEventListener('click', handleLinkClick);
                }
            })
            .catch(() => {
                // Silently fail if native app info unavailable
            });

        // Cleanup listener on unmount
        return () => {
            document.removeEventListener('click', handleLinkClick);
        };
    }, []);

    // This component doesn't render anything - it only manages event listeners
    return null;
};

export default GlobalLinkNativeAppInterceptor;
